import React from 'react';
import {
  Activity,
  Database,
  FileText,
  GitPullRequestArrow,
  ListChecks,
  Route,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { SectionDisclosure } from '../../../../components/ui/panel/SectionDisclosure';
import { cn } from '../../../../lib/utils';
import type {
  ProcessAssistantMode,
  ProcessAssistantRoot,
} from '../../assistant/processSurfacePlacement';
import { ProcessViewBox } from '../ProcessViewBox';

export interface ProcessModeSurfaceAction {
  id: string;
  label: string;
  description?: string;
  icon: LucideIcon;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  testId?: string;
}

interface ProcessModePrimaryCallout {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
}

interface ProcessModeFocusSurfaceProps {
  mode: ProcessAssistantMode;
  root: ProcessAssistantRoot;
  nodeCount: number;
  decisionCount: number;
  laneCount: number;
  pendingReviewCount: number;
  hasSource: boolean;
  hasRepositoryRecords: boolean;
  processRepositoryAvailable?: boolean;
  hasImprovementData: boolean;
  canWrite: boolean;
  onTidy: () => void | Promise<void>;
  onDraftFromNotes: () => void;
  onOpenProcessLibrary: () => void;
  onOpenReviewImports: () => void;
  onOpenViewDisplay: () => void;
  onAsk?: (prompt: string) => void | Promise<void>;
  className?: string;
}

const MODE_TITLE: Record<ProcessAssistantMode, string> = {
  'draw-flowchart': 'Current map',
  'draft-from-notes': 'Draft',
  'process-library': 'Process repository',
  'review-imports': 'Review',
  'improve-process': 'Improve process',
  'management-summary': 'Management',
  compliance: 'Compliance view',
  qms: 'QMS',
  'audit-review': 'Audit review',
  'custom-view': 'Custom view',
};

export const ProcessModeFocusSurface: React.FC<ProcessModeFocusSurfaceProps> = ({
  mode,
  root,
  nodeCount,
  decisionCount,
  laneCount,
  pendingReviewCount,
  hasSource,
  hasRepositoryRecords,
  processRepositoryAvailable = true,
  hasImprovementData,
  canWrite,
  onTidy,
  onDraftFromNotes,
  onOpenProcessLibrary,
  onOpenReviewImports,
  onOpenViewDisplay,
  onAsk,
  className,
}) => {
  const modeTitle = root.kind === 'view-process' ? 'View process' : MODE_TITLE[mode];
  const mapStatus = formatMapStatus(nodeCount, decisionCount, laneCount);
  const sourceStatus = hasSource ? 'Imported notes linked' : null;

  const ask = React.useCallback(
    (prompt: string) => {
      if (!onAsk) return;
      void onAsk(prompt);
    },
    [onAsk],
  );

  const actions = getActions({
    mode,
    nodeCount,
    pendingReviewCount,
    hasRepositoryRecords,
    processRepositoryAvailable,
    hasSource,
    hasImprovementData,
    canWrite,
    onTidy,
    onDraftFromNotes,
    onOpenProcessLibrary,
    onOpenReviewImports,
    onOpenViewDisplay,
    ask,
  });
  const primaryCallout = getPrimaryCallout({
    mode,
    nodeCount,
    pendingReviewCount,
    hasRepositoryRecords,
    processRepositoryAvailable,
    hasSource,
    hasImprovementData,
    canWrite,
    onDraftFromNotes,
    onOpenProcessLibrary,
    onOpenReviewImports,
    onOpenViewDisplay,
    ask,
  });

  const showViewBox =
    mode === 'custom-view' || (mode === 'draw-flowchart' && nodeCount > 0);

  return (
    <div
      className={cn('-mx-3 shrink-0', className)}
      data-mode={mode}
      data-testid="tid.process-mapping.assistant.mode-focus"
    >
      <SectionDisclosure
        id={`process-mapping.mode.${mode}`}
        title={modeTitle}
        defaultOpen
        compact
        hideTopBorder
      >
        <div className="space-y-3">
          {mode === 'audit-review' && (
            <p className="text-xs leading-relaxed text-muted-foreground">
              Walk through the process, inspect gaps, and compare it with repository evidence. Select an action below to begin the review.
            </p>
          )}
          <div className="min-w-0 truncate text-xs font-medium text-muted-foreground">
            {mode === 'review-imports'
              ? formatReviewStatus(pendingReviewCount)
              : mapStatus}
          </div>

          {showViewBox ? (
            <ProcessViewBox
              onChangeView={onOpenViewDisplay}
              showDefault={mode === 'custom-view'}
            />
          ) : null}

          {primaryCallout ? <PrimaryCallout callout={primaryCallout} /> : null}

          {actions.length > 0 ? <PrimaryActionRow actions={actions} /> : null}

          {sourceStatus ? <ContextStatus label={sourceStatus} /> : null}
        </div>
      </SectionDisclosure>
    </div>
  );
};

interface ActionContext {
  mode: ProcessAssistantMode;
  nodeCount: number;
  pendingReviewCount: number;
  hasRepositoryRecords: boolean;
  processRepositoryAvailable: boolean;
  hasSource: boolean;
  hasImprovementData: boolean;
  canWrite: boolean;
  onTidy: () => void | Promise<void>;
  onDraftFromNotes: () => void;
  onOpenProcessLibrary: () => void;
  onOpenReviewImports: () => void;
  onOpenViewDisplay: () => void;
  ask: (prompt: string) => void;
}

function getPrimaryCallout(
  ctx: Omit<ActionContext, 'onTidy'>,
): ProcessModePrimaryCallout | null {
  switch (ctx.mode) {
    case 'draw-flowchart':
      if (!ctx.canWrite || ctx.hasSource) return null;
      return {
        id: 'draft-from-notes',
        title: 'Draft from notes',
        description: 'Paste an SOP, interview, or rough steps.',
        icon: FileText,
        onClick: ctx.onDraftFromNotes,
      };
    case 'draft-from-notes':
      return {
        id: 'paste-import',
        title: ctx.hasSource ? 'Review source draft' : 'Paste or import notes',
        description: ctx.hasSource
          ? 'Check extracted steps before updating the process.'
          : 'Turn SOP text, transcripts, or rough notes into process steps.',
        icon: FileText,
        onClick: ctx.hasSource ? ctx.onOpenReviewImports : ctx.onDraftFromNotes,
      };
    case 'process-library':
      return {
        id: 'open-repository',
        title: ctx.hasRepositoryRecords ? 'Open process repository' : 'Create repository record',
        description: ctx.hasRepositoryRecords
          ? 'Review owners, source-backed records, and process metadata.'
          : 'Turn this map into a reusable process record.',
        icon: Database,
        onClick: ctx.onOpenProcessLibrary,
      };
    case 'review-imports':
      return {
        id: 'open-review',
        title: 'Review proposed changes',
        description: ctx.pendingReviewCount > 0
          ? 'Compare source changes before they update the map.'
          : 'No pending changes right now; open review history.',
        icon: GitPullRequestArrow,
        onClick: ctx.onOpenReviewImports,
        disabled: !ctx.canWrite && ctx.pendingReviewCount === 0,
      };
    case 'improve-process':
      if (!ctx.hasImprovementData) {
        return {
          id: 'missing-improvement-data',
          title: 'Add fields for deeper analysis',
          description: 'Owners, systems, SLAs, and risks unlock bottleneck and handoff checks.',
          icon: ListChecks,
          onClick: () => ctx.ask('List the owner, system, SLA, and risk fields missing from this process before deeper improvement analysis.'),
        };
      }
      return {
        id: 'improvement-scan',
        title: 'Run improvement scan',
        description: 'Find bottlenecks, risky handoffs, and owner gaps.',
        icon: Activity,
        onClick: () => ctx.ask('Find bottlenecks, risky handoffs, and ownership gaps in this process.'),
      };
    case 'management-summary':
      return {
        id: 'management-brief',
        title: 'Create leadership brief',
        description: 'Summarize risks, owners, handoffs, and next actions.',
        icon: Activity,
        onClick: () => ctx.ask('Create a management summary for this process.'),
      };
    case 'compliance':
      return {
        id: 'compliance-check',
        title: 'Check controls & evidence',
        description: 'Surface missing controls, weak evidence, and compliance gaps.',
        icon: ShieldCheck,
        onClick: () => ctx.ask('Check this process for control coverage, evidence strength, risks, and compliance gaps.'),
      };
    case 'qms':
      return {
        id: 'qms-readiness',
        title: 'Check QMS readiness',
        description: 'Review procedure quality, approvals, records, and audit readiness.',
        icon: ListChecks,
        onClick: () => ctx.ask('Check this process for QMS readiness, procedure quality, approvals, records, and audit readiness.'),
      };
    case 'audit-review':
      return {
        id: 'audit-walkthrough',
        title: 'Prepare audit walkthrough',
        description: 'Draft likely questions, evidence needs, and findings.',
        icon: GitPullRequestArrow,
        onClick: () => ctx.ask('Prepare an audit walkthrough for this process with likely questions, evidence needs, and findings.'),
      };
    case 'custom-view':
      return {
        id: 'views-lenses',
        title: 'Change views & lenses',
        description: 'Switch overlays, lenses, and review-focused process views.',
        icon: Sparkles,
        onClick: ctx.onOpenViewDisplay,
      };
  }
}

function getActions(ctx: ActionContext): readonly ProcessModeSurfaceAction[] {
  if (!ctx.canWrite) {
    if (ctx.mode === 'process-library') {
      return [
        {
          id: 'open-library',
          label: 'Open repository',
          icon: Database,
          onClick: ctx.onOpenProcessLibrary,
        },
      ];
    }

    if (ctx.mode === 'management-summary') {
      return [
        {
          id: 'summary',
          label: 'Summary',
          icon: Activity,
          onClick: () => ctx.ask('Create a management summary for this process.'),
        },
        {
          id: 'ownership-gaps',
          label: 'Owners',
          icon: ListChecks,
          onClick: () => ctx.ask('Find process steps with missing owners or unclear accountability.'),
        },
      ];
    }

    if (ctx.mode === 'compliance') {
      return [
        {
          id: 'controls',
          label: 'Controls',
          icon: ShieldCheck,
          onClick: () => ctx.ask('Summarize the controls linked to this process.'),
        },
        {
          id: 'evidence-gaps',
          label: 'Gaps',
          icon: ListChecks,
          onClick: () => ctx.ask('Summarize evidence gaps in this process.'),
        },
      ];
    }

    if (ctx.mode === 'qms') {
      return [
        {
          id: 'qms-check',
          label: 'QMS check',
          icon: ShieldCheck,
          onClick: () => ctx.ask('Check this process for QMS readiness.'),
        },
        {
          id: 'evidence-gaps',
          label: 'Gaps',
          icon: ListChecks,
          onClick: () => ctx.ask('Summarize procedure and evidence gaps in this process.'),
        },
      ];
    }

    if (ctx.mode === 'audit-review') {
      return [
        {
          id: 'walkthrough',
          label: 'Walkthrough',
          icon: GitPullRequestArrow,
          onClick: () => ctx.ask('Prepare an audit walkthrough for this process.'),
        },
        {
          id: 'evidence-gaps',
          label: 'Gaps',
          icon: ListChecks,
          onClick: () => ctx.ask('Summarize audit evidence gaps in this process.'),
        },
      ];
    }

    return [];
  }

  switch (ctx.mode) {
    case 'draw-flowchart':
      return [];
    case 'draft-from-notes':
      return [
        {
          id: 'review-draft',
          label: 'Review',
          icon: GitPullRequestArrow,
          onClick: ctx.onOpenReviewImports,
          disabled: ctx.pendingReviewCount === 0,
        },
        ...(ctx.processRepositoryAvailable
          ? [{
              id: 'library',
              label: 'Repository',
              icon: Database,
              onClick: ctx.onOpenProcessLibrary,
            }]
          : []),
      ];
    case 'process-library':
      return [
        {
          id: 'draft-from-notes',
          label: 'Import notes',
          icon: FileText,
          onClick: ctx.onDraftFromNotes,
        },
        {
          id: 'ownership-gaps',
          label: 'Owners',
          icon: ListChecks,
          onClick: () => ctx.ask('Find process steps with missing owners or unclear accountability.'),
          disabled: !ctx.hasRepositoryRecords,
        },
      ];
    case 'review-imports':
      return [
        ...(ctx.processRepositoryAvailable
          ? [{
              id: 'library',
              label: 'Repository',
              icon: Database,
              onClick: ctx.onOpenProcessLibrary,
            }]
          : []),
      ];
    case 'improve-process':
      if (!ctx.hasImprovementData) {
        return [
          {
            id: 'structure-scan',
            label: 'Structure',
            icon: Route,
            onClick: () => ctx.ask('Run a structure-only review of this process. Look for missing branches, disconnected steps, loops, and unclear endings.'),
          },
          {
            id: 'missing-fields',
            label: 'Fields',
            icon: ListChecks,
            onClick: () => ctx.ask('Find the process fields I should add for improvement analysis, including owners, systems, SLAs, and risks.'),
          },
          {
            id: 'tidy-layout',
            label: 'Tidy',
            icon: Sparkles,
            onClick: ctx.onTidy,
          },
        ];
      }
      return [
        {
          id: 'bottlenecks',
          label: 'Bottlenecks',
          icon: Activity,
          onClick: () => ctx.ask('Find the bottlenecks in this process.'),
        },
        {
          id: 'handoffs',
          label: 'Handoffs',
          icon: Route,
          onClick: () => ctx.ask('Highlight risky handoffs between teams or systems.'),
        },
        {
          id: 'tidy-layout',
          label: 'Tidy',
          icon: Sparkles,
          onClick: ctx.onTidy,
        },
      ];
    case 'management-summary':
      return [
        {
          id: 'summary',
          label: 'Summary',
          icon: Activity,
          onClick: () => ctx.ask('Create a management summary for this process.'),
        },
        {
          id: 'ownership-gaps',
          label: 'Owners',
          icon: ListChecks,
          onClick: () => ctx.ask('List owner actions.'),
        },
        {
          id: 'bottlenecks',
          label: 'Bottlenecks',
          icon: Route,
          onClick: () => ctx.ask('Find the bottlenecks in this process.'),
        },
      ];
    case 'compliance':
      return [
        {
          id: 'controls',
          label: 'Controls',
          icon: ShieldCheck,
          onClick: () => ctx.ask('Check this process for control and evidence gaps.'),
        },
        {
          id: 'evidence-gaps',
          label: 'Gaps',
          icon: ListChecks,
          onClick: () => ctx.ask('Find evidence gaps in this process.'),
        },
        {
          id: 'library',
          label: 'Repository',
          icon: Database,
          onClick: ctx.onOpenProcessLibrary,
        },
      ];
    case 'qms':
      return [
        {
          id: 'qms-check',
          label: 'QMS check',
          icon: ShieldCheck,
          onClick: () => ctx.ask('Check this process for QMS readiness, procedure quality, evidence coverage, approvals, and audit gaps.'),
        },
        {
          id: 'evidence',
          label: 'Evidence',
          icon: ListChecks,
          onClick: () => ctx.ask('Find missing procedure evidence, approvals, and quality records for this process.'),
        },
        {
          id: 'library',
          label: 'Repository',
          icon: Database,
          onClick: ctx.onOpenProcessLibrary,
        },
      ];
    case 'audit-review':
      return [
        {
          id: 'walkthrough',
          label: 'Walkthrough',
          icon: GitPullRequestArrow,
          onClick: () => ctx.ask('Prepare an audit review walkthrough for this process, including controls, evidence, findings, and follow-up questions.'),
        },
        {
          id: 'evidence-gaps',
          label: 'Gaps',
          icon: ListChecks,
          onClick: () => ctx.ask('Find audit evidence gaps and likely reviewer questions for this process.'),
        },
        {
          id: 'library',
          label: 'Repository',
          icon: Database,
          onClick: ctx.onOpenProcessLibrary,
        },
      ];
    case 'custom-view':
      return [];
  }
}

const PrimaryCallout: React.FC<{
  callout: ProcessModePrimaryCallout;
}> = ({ callout }) => {
  const Icon = callout.icon;

  return (
    <button
      type="button"
      onClick={() => void callout.onClick()}
      disabled={callout.disabled}
      className="flex w-full min-w-0 items-center gap-2 rounded-md border border-dashed border-border bg-muted/30 px-2.5 py-2 text-left transition-colors hover:border-blue-200 hover:bg-blue-50/60 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid={`tid.process-mapping.mode-focus.primary.${callout.id}`}
    >
      <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-background text-muted-foreground">
        <Icon className="size-3.5" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-medium leading-snug text-foreground">
          {callout.title}
        </span>
        <span className="block text-[11px] leading-snug text-muted-foreground">
          {callout.description}
        </span>
      </span>
    </button>
  );
};

const PrimaryActionRow: React.FC<{
  actions: readonly ProcessModeSurfaceAction[];
}> = ({ actions }) => {
  const visible = actions.slice(0, 3);

  return (
    <div
      className={cn(
        'grid overflow-hidden rounded-md border border-border',
        visible.length >= 3
          ? 'grid-cols-3'
          : visible.length === 2
            ? 'grid-cols-2'
            : 'grid-cols-1',
      )}
      role="group"
      aria-label="Process actions"
    >
      {visible.map((action, index) => {
        const Icon = action.icon;
        return (
          <button
            key={action.id}
            type="button"
            onClick={() => void action.onClick()}
            disabled={action.disabled}
            data-testid={action.testId}
            className={cn(
              'flex min-h-12 min-w-0 flex-col items-center justify-center gap-1 bg-background px-1.5 py-1.5 text-center text-[11px] font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50',
              index > 0 && 'border-l border-border',
            )}
          >
            <Icon className="size-3.5 text-muted-foreground" aria-hidden />
            <span className="max-w-full truncate">{action.label}</span>
          </button>
        );
      })}
    </div>
  );
};

const ContextStatus: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex min-w-0 items-center gap-2 border-t border-border/70 pt-2 text-[11px] text-muted-foreground">
    <FileText className="size-3.5 shrink-0" aria-hidden />
    <span className="min-w-0 flex-1 truncate">{label}</span>
  </div>
);

function formatMapStatus(
  nodeCount: number,
  decisionCount: number,
  laneCount: number,
): string {
  const parts = [nodeCount === 1 ? '1 step' : `${nodeCount} steps`];
  if (decisionCount > 0) {
    parts.push(decisionCount === 1 ? '1 decision' : `${decisionCount} decisions`);
  }
  if (laneCount > 0) {
    parts.push(laneCount === 1 ? '1 lane' : `${laneCount} lanes`);
  }
  return parts.join(' · ');
}

function formatReviewStatus(pendingReviewCount: number): string {
  if (pendingReviewCount === 1) return '1 change to review';
  return `${pendingReviewCount} changes to review`;
}
